/**
 * Django Messages Integration with Toast System
 * Handles automatic conversion of Django messages to toast notifications
 */

interface VariantConfig {
  toast: string;
  alert: string;
  heading: string;
}

interface ParsedMessage {
  message: string;
  heading: string;
  variant: string;
  fallbackVariant: string;
  autohide: number;
  node: HTMLElement;
  extraTags: string[];
}

interface ToastOptions {
  heading: string;
  message: string;
  variant: string;
  autohide: number;
}

interface MessageAPI {
  showDynamicMessage: (message: string, type?: string, autoHide?: boolean, heading?: string) => HTMLElement | null;
  showSuccessMessage: (message: string, autoHide?: boolean) => HTMLElement | null;
  showErrorMessage: (message: string, autoHide?: boolean) => HTMLElement | null;
  showInfoMessage: (message: string, autoHide?: boolean) => HTMLElement | null;
  showWarningMessage: (message: string, autoHide?: boolean) => HTMLElement | null;
  refreshMessageAutoHide: () => void;
  testMessages: () => void;
}

const TOAST_READY_EVENT = 'padelmatcher:ui-ready';
const MESSAGE_SELECTOR = '.pm-django-message';
const DEFAULT_AUTOHIDE = 6000;
const FALLBACK_ALERT_CLASS = 'pm-alert-fallback';

const VARIANT_CONFIG: Record<string, VariantConfig> = {
  success: { toast: 'success', alert: 'success', heading: 'Successo' },
  error: { toast: 'danger', alert: 'danger', heading: 'Errore' },
  danger: { toast: 'danger', alert: 'danger', heading: 'Errore' },
  warning: { toast: 'warning', alert: 'warning', heading: 'Attenzione' },
  info: { toast: 'info', alert: 'info', heading: 'Notifica' },
  debug: { toast: 'info', alert: 'secondary', heading: 'Debug' }
};

class MessageManager {
  constructor() {
    this.init();
  }

  private init(): void {
    document.addEventListener('DOMContentLoaded', () => {
      const queuedMessages = this.collectMessagesFromDom();
      if (!queuedMessages.length) {
        return;
      }

      const tryToastIntegration = (): boolean => {
        let success = false;
        try {
          success = this.showMessagesWithToast(queuedMessages);
          if (success) {
            this.removeMessageSourceNodes(queuedMessages);
          }
        } catch (error) {
          console.warn('pm-messages: errore integrazione toast, uso fallback Bootstrap', error);
          success = false;
        }
        return success;
      };

      if (tryToastIntegration()) {
        return;
      }

      const onUiReady = (): void => {
        if (tryToastIntegration()) {
          window.clearTimeout(fallbackTimer);
        }
      };

      window.addEventListener(TOAST_READY_EVENT, onUiReady, { once: true });

      const fallbackTimer = window.setTimeout(() => {
        window.removeEventListener(TOAST_READY_EVENT, onUiReady);
        this.removeMessageSourceNodes(queuedMessages);
        this.showFallbackAlerts(queuedMessages);
      }, 800);
    });
  }

  private isToastAvailable(): boolean {
    const window_any = window as any;
    return Boolean(window_any?.PadelMatcher?.ui?.showToast) && 
           Boolean(document.querySelector('pm-toast#pm-global-toast'));
  }

  private collectMessagesFromDom(): ParsedMessage[] {
    return Array.from(document.querySelectorAll(MESSAGE_SELECTOR))
      .map(node => this.parseMessageNode(node as HTMLElement))
      .filter((message): message is ParsedMessage => Boolean(message));
  }

  private parseMessageNode(node: HTMLElement): ParsedMessage | null {
    const messageText = (node.dataset.message || '').trim();
    if (!messageText) {
      return null;
    }

    const level = (node.dataset.messageLevel || '').toLowerCase();
    const tags = (node.dataset.messageTags || '')
      .split(' ')
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean);
    const extraTags = (node.dataset.messageExtraTags || '')
      .split(' ')
      .map((tag) => tag.trim())
      .filter(Boolean);

    const variantConfig = this.resolveVariantConfig(level, tags);
    const autohide = this.resolveAutohide(extraTags);
    const heading = this.resolveHeading(node.dataset.messageHeading, variantConfig, extraTags);

    return {
      message: messageText,
      heading,
      variant: variantConfig.toast,
      fallbackVariant: variantConfig.alert,
      autohide,
      node,
      extraTags
    };
  }

  private resolveVariantConfig(level: string, tags: string[]): VariantConfig {
    if (VARIANT_CONFIG[level]) {
      return VARIANT_CONFIG[level];
    }

    for (const tag of tags) {
      if (VARIANT_CONFIG[tag]) {
        return VARIANT_CONFIG[tag];
      }
      if (['danger', 'error'].includes(tag)) {
        return VARIANT_CONFIG.error;
      }
    }

    return VARIANT_CONFIG.info;
  }

  private resolveAutohide(extraTags: string[]): number {
    if (!extraTags.length) {
      return DEFAULT_AUTOHIDE;
    }

    for (const tag of extraTags) {
      if (tag === 'sticky' || tag === 'no-autohide' || tag === 'persistent') {
        return 0;
      }

      if (tag.startsWith('autohide-')) {
        const parsed = Number.parseInt(tag.replace('autohide-', ''), 10);
        if (Number.isFinite(parsed) && parsed >= 0) {
          return parsed;
        }
      }
    }

    return DEFAULT_AUTOHIDE;
  }

  private resolveHeading(customHeading: string | undefined, variantConfig: VariantConfig, extraTags: string[]): string {
    if (customHeading && customHeading.trim()) {
      return customHeading.trim();
    }

    const headingTag = extraTags.find((tag) => tag.startsWith('heading:'));
    if (headingTag) {
      return headingTag.split(':').slice(1).join(':').replace(/_/g, ' ').trim() || variantConfig.heading;
    }

    return variantConfig.heading;
  }

  private showMessagesWithToast(messages: ParsedMessage[]): boolean {
    if (!this.isToastAvailable()) {
      return false;
    }

    const toastElement = document.querySelector('pm-toast#pm-global-toast') as any;
    const window_any = window as any;
    const hideToast = window_any?.PadelMatcher?.ui?.hideToast;
    const showToast = window_any.PadelMatcher.ui.showToast;

    if (typeof showToast !== 'function') {
      return false;
    }

    const queue = messages.map((item) => ({ ...item }));

    const playNext = (): void => {
      const current = queue.shift();
      if (!current) {
        return;
      }

      const handleHidden = (): void => {
        toastElement.removeEventListener('pm-toast:hidden', handleHidden);
        playNext();
      };

      if (current.autohide > 0) {
        toastElement.addEventListener('pm-toast:hidden', handleHidden, { once: true });
      } else {
        // Se il toast è persistente, liberiamo la coda quando viene nascosto manualmente
        const manualHandler = (): void => {
          toastElement.removeEventListener('pm-toast:hidden', manualHandler);
          playNext();
        };
        toastElement.addEventListener('pm-toast:hidden', manualHandler, { once: true });
      }

      try {
        showToast({
          heading: current.heading,
          message: current.message,
          variant: current.variant,
          autohide: current.autohide
        });
      } catch (error) {
        console.error('pm-messages: impossibile mostrare toast, uso fallback', error);
        this.createBootstrapAlert(current.message, current.fallbackVariant, current.autohide);
        playNext();
      }
    };

    if (toastElement?.open && typeof hideToast === 'function') {
      const handleInitialHidden = (): void => {
        toastElement.removeEventListener('pm-toast:hidden', handleInitialHidden);
        playNext();
      };
      toastElement.addEventListener('pm-toast:hidden', handleInitialHidden, { once: true });
      hideToast();
    } else {
      playNext();
    }

    return true;
  }

  private showFallbackAlerts(messages: ParsedMessage[]): void {
    messages.forEach((item) => {
      this.createBootstrapAlert(item.message, item.fallbackVariant, item.autohide);
    });
  }

  private createBootstrapAlert(message: string, variant: string = 'info', autohide: number = DEFAULT_AUTOHIDE): HTMLElement {
    const alert = document.createElement('div');
    alert.className = `alert alert-${variant} alert-dismissible fade show ${FALLBACK_ALERT_CLASS}`;
    alert.setAttribute('role', 'alert');
    alert.textContent = message;

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'btn-close';
    closeBtn.setAttribute('data-bs-dismiss', 'alert');
    closeBtn.setAttribute('aria-label', 'Chiudi');
    alert.appendChild(closeBtn);

    const container = this.resolveMessageContainer();
    if (container.firstChild) {
      container.insertBefore(alert, container.firstChild);
    } else {
      container.appendChild(alert);
    }

    if (autohide > 0) {
      alert.dataset.pmAutoHide = String(autohide);
      window.setTimeout(() => this.dismissBootstrapAlert(alert), autohide);
    } else {
      alert.dataset.pmAutoHide = '0';
    }

    return alert;
  }

  private resolveMessageContainer(): HTMLElement {
    const candidates = [
      document.querySelector('main .container'),
      document.querySelector('main'),
      document.body
    ];

    return (candidates.find((el) => el instanceof HTMLElement) as HTMLElement) || document.body;
  }

  private dismissBootstrapAlert(alertElement: HTMLElement): void {
    if (!alertElement || !alertElement.parentNode) {
      return;
    }

    try {
      const bootstrap_any = (window as any).bootstrap;
      const instance = bootstrap_any?.Alert
        ? bootstrap_any.Alert.getOrCreateInstance(alertElement)
        : null;
      if (instance) {
        instance.close();
      } else {
        alertElement.classList.remove('show');
        alertElement.addEventListener(
          'transitionend',
          () => alertElement.remove(),
          { once: true }
        );
      }
    } catch (error) {
      console.warn('Bootstrap Alert non disponibile, rimozione diretta:', error);
      alertElement.remove();
    }
  }

  private removeMessageSourceNodes(messages: ParsedMessage[]): void {
    messages.forEach((item) => {
      if (item.node?.parentNode) {
        item.node.parentNode.removeChild(item.node);
      }
    });

    const sourceWrapper = document.getElementById('pm-django-messages-source');
    if (sourceWrapper && !sourceWrapper.children.length) {
      sourceWrapper.remove();
    }
  }

  public showDynamicMessage(message: string, type: string = 'info', autoHide: boolean = true, heading?: string): HTMLElement | null {
    const normalizedType = (type || 'info').toLowerCase();
    const variantConfig = VARIANT_CONFIG[normalizedType] || this.resolveVariantConfig(normalizedType, []);
    const autohide = autoHide ? DEFAULT_AUTOHIDE : 0;
    const resolvedHeading = heading || variantConfig.heading;

    const payload = {
      message,
      variant: variantConfig.toast,
      heading: resolvedHeading,
      autohide,
      fallbackVariant: variantConfig.alert
    };

    if (this.isToastAvailable()) {
      const window_any = window as any;
      window_any.PadelMatcher.ui.showToast({
        heading: payload.heading,
        message: payload.message,
        variant: payload.variant,
        autohide: payload.autohide
      });
      return null;
    }

    return this.createBootstrapAlert(payload.message, payload.fallbackVariant, payload.autohide);
  }

  public showSuccessMessage(message: string, autoHide: boolean = true): HTMLElement | null {
    return this.showDynamicMessage(message, 'success', autoHide);
  }

  public showErrorMessage(message: string, autoHide: boolean = true): HTMLElement | null {
    return this.showDynamicMessage(message, 'error', autoHide);
  }

  public showInfoMessage(message: string, autoHide: boolean = true): HTMLElement | null {
    return this.showDynamicMessage(message, 'info', autoHide);
  }

  public showWarningMessage(message: string, autoHide: boolean = true): HTMLElement | null {
    return this.showDynamicMessage(message, 'warning', autoHide);
  }

  public refreshMessageAutoHide(): void {
    document.querySelectorAll(`.${FALLBACK_ALERT_CLASS}`).forEach((alert) => {
      const alertElement = alert as HTMLElement;
      if (!alertElement.isConnected) {
        return;
      }

      const autohide = Number.parseInt(alertElement.dataset.pmAutoHide || `${DEFAULT_AUTOHIDE}`, 10);
      if (Number.isFinite(autohide) && autohide > 0) {
        window.setTimeout(() => this.dismissBootstrapAlert(alertElement), autohide);
      }
    });
  }

  public testMessages(): void {
    const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

    (async () => {
      this.showSuccessMessage('Messaggio di successo - verifica integrazione Lit');
      await delay(800);
      this.showErrorMessage('Messaggio di errore - verifica integrazione Lit');
      await delay(800);
      this.showInfoMessage('Messaggio informativo - verifica integrazione Lit');
      await delay(800);
      this.showWarningMessage('Messaggio di avviso - verifica integrazione Lit');
    })();
  }
}

// Initialize and expose API
const messageManager = new MessageManager();

const messageAPI: MessageAPI = {
  showDynamicMessage: (message: string, type?: string, autoHide?: boolean, heading?: string) => 
    messageManager.showDynamicMessage(message, type, autoHide, heading),
  showSuccessMessage: (message: string, autoHide?: boolean) => 
    messageManager.showSuccessMessage(message, autoHide),
  showErrorMessage: (message: string, autoHide?: boolean) => 
    messageManager.showErrorMessage(message, autoHide),
  showInfoMessage: (message: string, autoHide?: boolean) => 
    messageManager.showInfoMessage(message, autoHide),
  showWarningMessage: (message: string, autoHide?: boolean) => 
    messageManager.showWarningMessage(message, autoHide),
  refreshMessageAutoHide: () => messageManager.refreshMessageAutoHide(),
  testMessages: () => messageManager.testMessages()
};

// Extend global window object
declare global {
  interface Window {
    showDynamicMessage: (message: string, type?: string, autoHide?: boolean, heading?: string) => HTMLElement | null;
    showSuccessMessage: (message: string, autoHide?: boolean) => HTMLElement | null;
    showErrorMessage: (message: string, autoHide?: boolean) => HTMLElement | null;
    showInfoMessage: (message: string, autoHide?: boolean) => HTMLElement | null;
    showWarningMessage: (message: string, autoHide?: boolean) => HTMLElement | null;
    refreshMessageAutoHide: () => void;
    testMessages: () => void;
  }
}

window.showDynamicMessage = messageAPI.showDynamicMessage;
window.showSuccessMessage = messageAPI.showSuccessMessage;
window.showErrorMessage = messageAPI.showErrorMessage;
window.showInfoMessage = messageAPI.showInfoMessage;
window.showWarningMessage = messageAPI.showWarningMessage;
window.refreshMessageAutoHide = messageAPI.refreshMessageAutoHide;
window.testMessages = messageAPI.testMessages;

export { MessageManager, messageAPI };
export type { MessageAPI, ToastOptions, ParsedMessage, VariantConfig };